import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Plus, Users as UsersIcon, Trash2, Key, Power, AlertTriangle } from 'lucide-react';
import { formatRelativeTime } from '../lib/utils';
import { useConfirm } from '../components/ui/ConfirmDialog';

export function UsersPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);

  const list = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.get<any[]>('/api/v1/users'),
  });

  const create = useMutation({
    mutationFn: (d: any) => apiClient.post('/api/v1/users', d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setOpen(false);
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const hardDelete = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/users/${id}?hard=true`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <UsersIcon size={22} className="text-neon-cyan" />
            User Management
          </h1>
          <p className="text-sm text-text-secondary font-mono mt-1">// {list.data?.length ?? 0} users</p>
        </div>
        <Button icon={<Plus size={14} />} onClick={() => setOpen(true)}>New User</Button>
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Username</th>
                <th>Display Name</th>
                <th>Role</th>
                <th>Publisher</th>
                <th>Status</th>
                <th>Last Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((u) => (
                <tr key={u.id}>
                  <td className="font-mono text-sm">{u.username}</td>
                  <td className="text-sm">{u.displayName ?? <span className="text-text-tertiary">-</span>}</td>
                  <td>
                    <Badge variant={u.role === 'admin' ? 'info' : 'live'}>{u.role}</Badge>
                  </td>
                  <td className="font-mono text-xs">
                    {u.publisherId ? `#${u.publisherId}` : <span className="text-text-tertiary">-</span>}
                  </td>
                  <td>
                    {u.isActive ? <StatusBadge status="active" /> : <Badge variant="archived">disabled</Badge>}
                    {u.mustChangePassword && <Badge variant="pending" className="ml-1">pwd</Badge>}
                  </td>
                  <td className="text-xs text-text-tertiary font-mono">
                    {u.lastLoginAt ? formatRelativeTime(u.lastLoginAt) : 'never'}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Disable (soft) — only when active */}
                      {u.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Power size={12} />}
                          onClick={() =>
                            confirm({
                              title: `Disable ${u.username}?`,
                              description: 'User tidak bisa login lagi. Bisa di-enable nanti via API.',
                              variant: 'warning',
                              actions: [{ label: 'Disable', variant: 'magenta', result: 'go', icon: <Power size={14} /> }],
                            }).then((r) => { if (r === 'go') deactivate.mutate(u.id); })
                          }
                          title="Disable (soft — keep audit trail)"
                          className="text-neon-yellow hover:opacity-70"
                        />
                      )}
                      {/* Delete (hard) — permanent */}
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 size={12} />}
                        onClick={() =>
                          confirm({
                            title: `Delete ${u.username}?`,
                            description: 'Permanently removes the user. Audit log entries akan kehilangan reference ke user ini.',
                            variant: 'danger',
                            actions: [{ label: 'Delete', variant: 'danger', result: 'go', icon: <Trash2 size={14} /> }],
                          }).then((r) => { if (r === 'go') hardDelete.mutate(u.id); })
                        }
                        title="Delete permanently"
                        className="text-neon-red hover:opacity-70"
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {(list.data ?? []).length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-text-tertiary">
                    <UsersIcon className="mx-auto mb-2" size={32} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Create New User" size="lg">
        <UserForm
          onSubmit={(d: any) => create.mutate(d)}
          onCancel={() => setOpen(false)}
          loading={create.isPending}
          error={create.error ? (create.error as Error).message : null}
        />
      </Modal>
    </div>
  );
}

function UserForm({ onSubmit, onCancel, loading, error }: any) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [displayName, setDisplayName] = useState('');
  const [subId, setSubId] = useState('');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const payload: any = { username, password, email: email || undefined, role, displayName: displayName || undefined };
        if (role === 'user') {
          payload.inlinePublisher = { subId, displayName: displayName || undefined };
        }
        onSubmit(payload);
      }}
      className="w-full space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Username" value={username} onChange={(e) => setUsername(e.target.value)} required />
        <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} hint="≥ 8 chars" required />
      </div>
      <Input label="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <Input label="Display Name (optional)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      <div>
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">Role</label>
        <div className="flex gap-2">
          {(['admin', 'user'] as const).map((r) => (
            <button
              type="button"
              key={r}
              onClick={() => setRole(r)}
              className={`flex-1 px-4 py-2 text-sm font-display uppercase tracking-wider rounded border transition-all ${
                role === r ? 'border-neon-cyan text-neon-cyan bg-neon-cyan/5 shadow-neon-cyan' : 'border-border-subtle text-text-secondary'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {role === 'user' && (
        <div className="cyber-panel p-4 space-y-3 border-neon-magenta/40">
          <div className="text-[10px] font-display uppercase tracking-widest text-neon-magenta flex items-center gap-2">
            <Key size={12} /> Inline Publisher (linked to this user)
          </div>
          <Input label="Sub ID" value={subId} onChange={(e) => setSubId(e.target.value)} placeholder="un-amad" required hint="Slug yang dipakai network (iMonetizeIt/Trafee/dll) sebagai sub_id parameter." />
        </div>
      )}

      {error && (
        <div className="rounded border border-neon-red/50 bg-neon-red/10 px-3 py-2 text-xs font-mono text-neon-red flex gap-1.5 items-center">
          <AlertTriangle size={12} className="inline shrink-0" /> {error}
        </div>
      )}
      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={loading} icon={<Plus size={14} />}>Create User</Button>
      </div>
    </form>
  );
}
